import React, { useState, useEffect, useRef } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import axiosInstance from '../../../utils/axiosInstance';
import { FaUser, FaEnvelope, FaCaretDown, FaCaretUp, FaCreditCard, FaNewspaper, FaBuilding } from 'react-icons/fa';
import './UserInfo.css';
import { capitalizeFirstLetter } from '../../../helpers/stringHelpers';
import { runSecurityTests } from '../../../utils/securityTest';

// Form validation helper
const validateForm = (formData) => {
  const errors = {};
  if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    errors.email = 'Please enter a valid email address';
  }
  if (formData.phone && !/^\+?[\d\s-]{10,}$/.test(formData.phone)) {
    errors.phone = 'Please enter a valid phone number';
  }
  if (formData.newPassword && formData.newPassword.length < 8) {
    errors.newPassword = 'Password must be at least 8 characters long';
  }
  if (formData.newPassword !== formData.confirmNewPassword) {
    errors.confirmNewPassword = 'Passwords do not match';
  }
  return errors;
};

function UserInfo({ userId, name, email, phone, setRefreshTrigger, onCardDetailsUpdate }) {
  // Form state
  const [formData, setFormData] = useState({
    name: name || '',
    email: email || '',
    // phone: phone || '',
    primaryLocation: '', // Store the location ID here
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });

  // UI state
  const [isEditing, setIsEditing] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showCardDetails, setShowCardDetails] = useState(false);
  const [isSavingCard, setIsSavingCard] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
  
  // Loading states
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Error state
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState(null);

  // Card state
  const [cardDetails, setCardDetails] = useState(null);

  // Newsletter subscription state
  const [newsletterSubscription, setNewsletterSubscription] = useState(false);
  const [isTogglingNewsletter, setIsTogglingNewsletter] = useState(false);
  
  // Newsletter send state
  const [isSendingNewsletter, setIsSendingNewsletter] = useState(false);
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);
  const [newsletterModalMessage, setNewsletterModalMessage] = useState('');

  // Primary location state
  const [primaryLocation, setPrimaryLocation] = useState(null);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [locationSearchTerm, setLocationSearchTerm] = useState('');
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);

  // Refs for animations
  const editContainerRef = useRef(null);
  const cardDetailsRef = useRef(null);
  const locationDropdownRef = useRef(null);
  
  // Height states for smooth dropdown animations
  const [cardDetailsHeight, setCardDetailsHeight] = useState(0);
  const [editContainerHeight, setEditContainerHeight] = useState(0);

  // Stripe setup
  const stripe = useStripe();
  const elements = useElements();

  // Track edit container height for smooth animation (similar to card details)
  useEffect(() => {
    if (isEditing && editContainerRef.current) {
      setEditContainerHeight(editContainerRef.current.scrollHeight);
    } else {
      setEditContainerHeight(0);
    }
  }, [isEditing, showChangePassword, formData.primaryLocation, showLocationDropdown, locationSearchTerm, availableLocations.length]);

  // Track card details dropdown height for smooth animation
  useEffect(() => {
    if (showCardDetails && cardDetailsRef.current) {
      setCardDetailsHeight(cardDetailsRef.current.scrollHeight);
    } else {
      setCardDetailsHeight(0);
    }
  }, [showCardDetails, cardDetails, isSavingCard]);

  // Fetch saved card, newsletter subscription, and available locations on mount
  useEffect(() => {
    fetchSavedCard();
    fetchNewsletterSubscription();
    fetchAvailableLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch newsletter subscription and primary location when tab becomes visible
  // This ensures data is fresh if changed from admin panel
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchNewsletterSubscription();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close location dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target)) {
        setShowLocationDropdown(false);
      }
    };

    if (showLocationDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLocationDropdown]);

  const fetchSavedCard = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get('/users/card-details', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const card = response.data.card || null;
      setCardDetails(card);
      // Update parent component's state
      if (onCardDetailsUpdate) {
        onCardDetailsUpdate(card);
      }
    } catch (error) {
      // Only show error if it's not a 404 (no card found) error
      if (error.response && error.response.status !== 404) {
        console.error('Failed to fetch saved card details:', error);
        setApiError('Failed to load card details. Please try again later.');
      } else {
        setCardDetails(null);
        // Update parent component's state
        if (onCardDetailsUpdate) {
          onCardDetailsUpdate(null);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch all available locations for the dropdown
  const fetchAvailableLocations = async () => {
    try {
      const response = await axiosInstance.get('/locations/public');
      setAvailableLocations(response.data || []);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      setAvailableLocations([]);
    }
  };

  // Fetch newsletter subscription status and primary location
  const fetchNewsletterSubscription = async () => {
    try {
      const response = await axiosInstance.get('/users/profile', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      
      // Set the newsletter subscription status from user data
      // Using ?? instead of || to handle explicit false values correctly
      const subscriptionValue = response.data.newsletterSubscription ?? false;
      setNewsletterSubscription(subscriptionValue);
      
      // Handle primary location
      if (response.data.primaryLocation) {
        const locationData = response.data.primaryLocation;
        
        // Check if it's already populated with location details (has a 'name' field)
        if (typeof locationData === 'object' && locationData.name) {
          setPrimaryLocation(locationData);
          // Also set it in the form data for editing
          setFormData(prev => ({
            ...prev,
            primaryLocation: locationData._id
          }));
        } 
        // If it's just an ID string, fetch the location details
        else if (typeof locationData === 'string') {
          try {
            const locationResponse = await axiosInstance.get('/locations/public');
            const location = locationResponse.data.find(loc => loc._id === locationData);
            if (location) {
              setPrimaryLocation(location);
              // Also set it in the form data for editing
              setFormData(prev => ({
                ...prev,
                primaryLocation: location._id
              }));
            }
          } catch (error) {
            console.error('Failed to fetch location details:', error);
            setPrimaryLocation(null);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      // Default to false if we can't fetch the status
      setNewsletterSubscription(false);
      setPrimaryLocation(null);
    }
  };

  // Toggle newsletter subscription
  const handleToggleNewsletter = async () => {
    try {
      setIsTogglingNewsletter(true);
      const response = await axiosInstance.patch(
        '/users/newsletter-subscription',
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }
      );
      
      // Update local state with the new subscription status from backend
      setNewsletterSubscription(response.data.newsletterSubscription);
    } catch (error) {
      console.error('Failed to toggle newsletter subscription:', error);
      setApiError('Failed to update newsletter subscription. Please try again.');
    } finally {
      setIsTogglingNewsletter(false);
    }
  };

  // Send newsletter to user immediately
  const handleSendNewsletter = async () => {
    try {
      setIsSendingNewsletter(true);
      
      // Make POST request to send newsletter
      const response = await axiosInstance.post(
        '/newsletters/send',
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }
      );
      
      // Show success message with recipient email
      setNewsletterModalMessage(`Newsletter sent successfully to ${response.data.recipient}!`);
      setShowNewsletterModal(true);
      
      // Auto-close modal after 3 seconds
      setTimeout(() => {
        setShowNewsletterModal(false);
      }, 3000);
    } catch (error) {
      console.error('Failed to send newsletter:', error);
      
      // Show error message
      setNewsletterModalMessage('Failed to send newsletter. Please try again.');
      setShowNewsletterModal(true);
      
      // Auto-close modal after 3 seconds
      setTimeout(() => {
        setShowNewsletterModal(false);
      }, 3000);
    } finally {
      setIsSendingNewsletter(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Handle location search input change
  const handleLocationSearchChange = (e) => {
    const value = e.target.value;
    setLocationSearchTerm(value);
    setShowLocationDropdown(true);
    
    // If user clears the field, clear the selected location too
    if (value === '') {
      setFormData(prev => ({
        ...prev,
        primaryLocation: ''
      }));
    }
  };

  // Handle location selection from dropdown
  const handleLocationSelect = (location) => {
    setFormData(prev => ({
      ...prev,
      primaryLocation: location._id
    }));
    setLocationSearchTerm(location.name);
    setShowLocationDropdown(false);
  };

  // Handle location input focus - populate search term with current selection if empty
  const handleLocationInputFocus = () => {
    if (!locationSearchTerm && formData.primaryLocation) {
      const selected = availableLocations.find(loc => loc._id === formData.primaryLocation);
      if (selected) {
        setLocationSearchTerm(selected.name);
      }
    }
    setShowLocationDropdown(true);
  };

  // Filter locations based on search term
  const filteredLocations = availableLocations.filter(location =>
    location.name.toLowerCase().includes(locationSearchTerm.toLowerCase())
  );

  // Calculate dynamic margin based on number of locations (max 4 visible)
  const getDropdownMargin = () => {
    const count = Math.min(filteredLocations.length, 4);
    const margins = {
      0: '1.5em',  // No results
      1: '4rem',   // One location
      2: '5.5rem', // Two locations
      3: '7rem',   // Three locations
      4: '8.5rem'  // Four or more locations
    };
    return margins[count] || '1.5em';
  };

  const toggleEditMode = () => {
    if (isEditing) {
      // Reset form data when canceling
      setFormData({
        name: name || '',
        email: email || '',
        // phone: phone || '',
        primaryLocation: primaryLocation?._id || '',
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
      });
      setErrors({});
      setShowChangePassword(false);
      setLocationSearchTerm('');
      setShowLocationDropdown(false);
    } else {
      // When opening edit mode, populate the search term with current location name
      if (primaryLocation?.name) {
        setLocationSearchTerm(primaryLocation.name);
      }
    }
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    try {
      // Validate form
      const validationErrors = validateForm(formData);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      // Run security tests
      const securityResults = runSecurityTests(
        formData.name + 
        formData.email + 
        (formData.phone || '') + 
        (formData.currentPassword || '') + 
        (formData.newPassword || '') + 
        (formData.confirmNewPassword || '')
      );
      
      if (Object.values(securityResults).some(result => result)) {
        setErrors({ general: 'Invalid input detected' });
        return;
      }

      setIsSaving(true);
      setApiError(null);

      if (showChangePassword) {
        await handlePasswordChange();
      } else {
        await handleProfileUpdate();
      }

      setIsEditing(false);
      setLocationSearchTerm('');
      setShowLocationDropdown(false);
      setRefreshTrigger(prev => !prev);
    } catch (error) {
      console.error('Failed to save user details:', error);
      setApiError('Failed to update details. Please try again later.');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    // Run security tests specifically for password fields
    const passwordSecurityResults = runSecurityTests(
      formData.currentPassword + 
      formData.newPassword + 
      formData.confirmNewPassword
    );
    
    if (Object.values(passwordSecurityResults).some(result => result)) {
      setErrors({ general: 'Invalid password input detected' });
      return;
    }

    await axiosInstance.patch(
      `/users/profile/${userId}/update-password`,
      {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      },
      {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      }
    );
  };

  const handleProfileUpdate = async () => {
    // Run security tests for profile fields
    const profileSecurityResults = runSecurityTests(
      formData.name + 
      formData.email + 
      (formData.phone || '')
    );
    
    if (Object.values(profileSecurityResults).some(result => result)) {
      setErrors({ general: 'Invalid profile input detected' });
      return;
    }

    if (formData.email !== email) {
      await axiosInstance.post(
        '/users/request-email-change',
        { newEmail: formData.email },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }
      );
      setShowEmailChangeModal(true);
    }

    // Check if name, phone, or primaryLocation has changed
    const currentLocationId = primaryLocation?._id || '';
    const hasChanges = 
      formData.name !== name || 
      formData.phone !== phone || 
      formData.primaryLocation !== currentLocationId;

    if (hasChanges) {
      const updateData = {
        name: formData.name,
        phoneNumber: formData.phone
      };
      
      // Only include primaryLocation if it has a value
      if (formData.primaryLocation) {
        updateData.primaryLocation = formData.primaryLocation;
      }

      await axiosInstance.patch(
        `/users/profile/${userId}`,
        updateData,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }
      );
    }
  };

  const handleSubmitCard = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) {
      setApiError('Payment system is not ready. Please try again.');
      return;
    }

    try {
      setIsSavingCard(true);
      const cardElement = elements.getElement(CardElement);
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        throw new Error(error.message);
      }

      const response = await axiosInstance.post(
        '/users/add-card',
        { paymentMethodId: paymentMethod.id },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      setCardDetails(response.data.card);
      setRefreshTrigger(prev => !prev);
      setIsSavingCard(false);
    } catch (error) {
      console.error('Failed to save card:', error);
      setApiError('Failed to save card. Please try again.');
    }
  };

  const handleDeleteCard = async () => {
    try {
      await deleteCardAndHandleSubscriptions();
    } catch (error) {
      console.error('Failed to delete card:', error);
      setApiError('Failed to delete card. Please try again later.');
    }
  };

  const deleteCardAndHandleSubscriptions = async () => {
    try {
      // Delete the card
      await axiosInstance.delete('/users/delete-card', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      // Update local state
      setCardDetails(null);
      if (onCardDetailsUpdate) {
        onCardDetailsUpdate(null);
      }
      setShowCardDetails(false);
      setShowDeleteModal(false);
      setRefreshTrigger(prev => !prev);
    } catch (error) {
      console.error('Failed to delete card:', error);
      throw error;
    }
  };


  return (
    <div className="day-pass-stats-container">
      <div className="day-pass-stats-title">
        <FaUser className="title-icon" /> Your Details
      </div>

      {apiError && (
        <div className="error-message">
          {apiError}
        </div>
      )}

      <div className="day-pass-stats-info-header">
        <FaUser className="active-memberships-icon" /> Account Details
        <button 
          className="edit-user-info-button" 
          onClick={toggleEditMode}
          style={{ position: 'absolute', right: '0', top: '50%', transform: 'translateY(-50%)' }}
        >
          {isEditing ? 'Cancel' : 'Edit Details / Change Password'}
        </button>
      </div>

      <div 
        className="day-pass-stats-memberships" 
        ref={editContainerRef}
        style={{ height: isEditing ? `${editContainerHeight}px` : '0' }}
      >
        {isEditing && (
          <div className="membership-details">
            <div className="membership-detail">
              <div className="membership-detail-title">Name</div>
              <div className="membership-detail-middle">
                <input
                  className={`user-info-input ${errors.name ? 'error' : ''}`}
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Name"
                />
                {errors.name && <span className="error-text">{errors.name}</span>}
              </div>
            </div>

            <div className="membership-detail">
              <div className="membership-detail-title">Email</div>
              <div className="membership-detail-middle">
                <input
                  className={`user-info-input ${errors.email ? 'error' : ''}`}
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Email"
                />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>
            </div>

            {/* Primary Location Dropdown Field */}
            <div 
              className={`membership-detail location-field ${showLocationDropdown ? 'dropdown-open' : ''}`}
              style={showLocationDropdown ? { marginBottom: getDropdownMargin() } : {}}
            >
              <div className="membership-detail-title">Primary Location</div>
              <div className="membership-detail-middle" ref={locationDropdownRef}>
                <input
                  className="user-info-input location-search-input"
                  type="text"
                  value={locationSearchTerm}
                  onChange={handleLocationSearchChange}
                  onFocus={handleLocationInputFocus}
                  placeholder="Select a location / Search"
                  autoComplete="off"
                />
                {showLocationDropdown && (
                  <div className="location-dropdown">
                    {filteredLocations.length > 0 ? (
                      filteredLocations.map(location => (
                        <div
                          key={location._id}
                          className="location-dropdown-item"
                          onClick={() => handleLocationSelect(location)}
                        >
                          {location.name}
                        </div>
                      ))
                    ) : (
                      <div className="location-dropdown-item no-results">
                        No locations found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Commenting out phone field
            <div className="membership-detail">
              <div className="membership-detail-title">Phone</div>
              <div className="membership-detail-middle">
                <input
                  className={`user-info-input ${errors.phone ? 'error' : ''}`}
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Phone Number"
                />
                {errors.phone && <span className="error-text">{errors.phone}</span>}
              </div>
            </div>
            */}

            {showChangePassword && (
              <>
                {/* Separator line between user details and password section */}
                <div className="password-section-divider"></div>
                
                <div className="membership-detail">
                  <div className="membership-detail-title">Current Password</div>
                  <div className="membership-detail-middle">
                    <input
                      className={`user-info-input ${errors.currentPassword ? 'error' : ''}`}
                      type="password"
                      name="currentPassword"
                      value={formData.currentPassword}
                      onChange={handleInputChange}
                      placeholder="Current Password"
                    />
                    {errors.currentPassword && <span className="error-text">{errors.currentPassword}</span>}
                  </div>
                </div>

                <div className="membership-detail">
                  <div className="membership-detail-title">New Password</div>
                  <div className="membership-detail-middle">
                    <input
                      className={`user-info-input ${errors.newPassword ? 'error' : ''}`}
                      type="password"
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      placeholder="New Password"
                    />
                    {errors.newPassword && <span className="error-text">{errors.newPassword}</span>}
                  </div>
                </div>

                <div className="membership-detail">
                  <div className="membership-detail-title">Confirm Password</div>
                  <div className="membership-detail-middle">
                    <input
                      className={`user-info-input ${errors.confirmNewPassword ? 'error' : ''}`}
                      type="password"
                      name="confirmNewPassword"
                      value={formData.confirmNewPassword}
                      onChange={handleInputChange}
                      placeholder="Confirm New Password"
                    />
                    {errors.confirmNewPassword && <span className="error-text">{errors.confirmNewPassword}</span>}
                  </div>
                </div>
              </>
            )}

            <div className="membership-detail-buttons">
              <button
                className="save-user-info-button"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : showChangePassword ? 'Change Password' : 'Save Details'}
              </button>
              {!showChangePassword && (
                <button
                  className="change-password-link"
                  onClick={() => setShowChangePassword(prev => !prev)}
                >
                  Change Password
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="day-pass-stats-info-header">
        <FaUser className="active-memberships-icon" /> Name:
        <div className="day-pass-stats-info-answer">{name}</div>
      </div>

      <div className="day-pass-stats-info-header">
        <FaEnvelope className="total-day-passes-icon" /> Email:
        <div className="day-pass-stats-info-answer">{email}</div>
      </div>

      {/* Commenting out phone display
      <div className="day-pass-stats-info-header">
        <FaPhone className="check-ins-icon" /> Phone:
        <div className="day-pass-stats-info-answer">{phone}</div>
      </div>
      */}

      {/* Primary Location Display */}
      <div className="day-pass-stats-info-header">
        <FaBuilding className="check-ins-icon" /> Primary Location:
        <div className="day-pass-stats-info-answer primary-location-answer">
          {primaryLocation ? (primaryLocation.name || primaryLocation) : 'Not set'}
        </div>
      </div>

      {/* Saved Card Section - wrapped in container to keep header and dropdown together */}
      <div className="saved-card-section">
        <div className="day-pass-stats-info-header saved-card-header" onClick={() => setShowCardDetails(!showCardDetails)}>
          <div className="saved-card-left">
            <FaCreditCard className="first-visit-date-icon" /> Saved Card:
            <div className="card-toggle-icon">
              {showCardDetails ? <FaCaretUp className="caret-icon" /> : <FaCaretDown className="caret-icon" />}
            </div>
          </div>
          <div className="day-pass-stats-info-answer">
            {cardDetails ? `${capitalizeFirstLetter(cardDetails.brand)} ...${cardDetails.last4}` : 'No card saved'}
          </div>
        </div>

        <div 
          className="day-pass-stats-memberships" 
          ref={cardDetailsRef}
          style={{ height: showCardDetails ? `${cardDetailsHeight}px` : '0' }}
        >
          <div className="membership-details">
            {isLoading ? (
              <div className="loading-spinner">Loading...</div>
            ) : cardDetails && !isSavingCard ? (
              <>
                <div className="membership-detail">
                  <div className="membership-detail-middle">
                    <p className="card-info-text">
                      {capitalizeFirstLetter(cardDetails.brand)} **** **** **** {cardDetails.last4}
                    </p>
                    <p className="card-info-text">
                      Expiry: {cardDetails.exp_month}/{cardDetails.exp_year}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center', marginTop: '10px' }}>
                  <button 
                    className="cancel-user-info-button" 
                    onClick={() => setIsSavingCard(true)}
                  >
                    Change Card
                  </button>
                  <button
                    className="cancel-user-info-button"
                    onClick={() => setShowDeleteModal(true)}
                  >
                    Remove Card
                  </button>
                </div>
              </>
            ) : isSavingCard ? (
              <form onSubmit={handleSubmitCard} className="card-form-container">
                <div className="card-element-container">
                  <CardElement
                    options={{
                      style: {
                        base: {
                          fontSize: '16px',
                          color: '#424770',
                          '::placeholder': {
                            color: '#aab7c4',
                          },
                        },
                        invalid: {
                          color: '#9e2146',
                        },
                      },
                    }}
                  />
                </div>
                <div className="card-form-buttons">
                  <button type="submit" className="save-user-info-button" disabled={isSavingCard}>
                    Save Card
                  </button>
                  <button
                    type="button"
                    className="cancel-user-info-button"
                    onClick={() => {
                      setIsSavingCard(false);
                      fetchSavedCard();
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="card-details-dropdown">
                <button 
                  className="save-card-button" 
                  onClick={() => setIsSavingCard(true)}
                >
                  Save a Card
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Newsletter Subscription Toggle - label and toggle grouped so slider stays next to text */}
      <div className="day-pass-stats-info-header newsletter-header">
        <div className="newsletter-label-and-toggle">
          <span className="newsletter-left">
            <FaNewspaper className="newsletter-icon" /> Receive Monthly Newsletter
          </span>
          <div className="newsletter-toggle-container">
            <label className="toggle-switch">
            <input
              type="checkbox"
              checked={newsletterSubscription}
              onChange={handleToggleNewsletter}
              disabled={isTogglingNewsletter}
            />
            <span className="toggle-slider"></span>
          </label>
          </div>
        </div>
      </div>
      <div className="newsletter-description">
        Stay updated with your location's upcoming events! Our monthly newsletter delivers a curated list of activities and special events happening at your primary location.
        {' '}
        <span 
          className="newsletter-send-link"
          onClick={handleSendNewsletter}
          style={{ opacity: isSendingNewsletter ? 0.5 : 1, cursor: isSendingNewsletter ? 'wait' : 'pointer' }}
        >
          {isSendingNewsletter ? 'Sending...' : 'Click here to receive this month\'s newsletter now.'}
        </span>
      </div>

      {/* Newsletter Send Confirmation Modal */}
      {showNewsletterModal && (
        <div className="email-change-modal" onClick={() => setShowNewsletterModal(false)}>
          <div className="modal-content newsletter-modal-content" onClick={e => e.stopPropagation()}>
            <button
              className="modal-close-button"
              onClick={() => setShowNewsletterModal(false)}
            >
              ×
            </button>
            <p>{newsletterModalMessage}</p>
            <button
              className="close-modal-button"
              onClick={() => setShowNewsletterModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {showEmailChangeModal && (
        <div className="email-change-modal" onClick={() => setShowEmailChangeModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button
              className="modal-close-button"
              onClick={() => setShowEmailChangeModal(false)}
            >
              ×
            </button>
            <p>
              A confirmation email has been sent to your new email address. Please
              confirm to complete the change.
            </p>
            <button
              className="close-modal-button"
              onClick={() => setShowEmailChangeModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="delete-card-modal" onClick={() => setShowDeleteModal(false)}>
          <div className="delete-card-modal-content" onClick={e => e.stopPropagation()}>
            <h3>Delete Card</h3>
            <p>Are you sure you want to delete your saved card?</p>
            <div className="delete-card-modal-buttons">
              <button type="button" className="delete-card-cancel-btn" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button type="button" className="delete-card-confirm-btn" onClick={handleDeleteCard}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserInfo;
